/**
 * Diálogo de confirmación.
 *
 * No usa `window.confirm` por dos motivos concretos: el nativo no se puede
 * redactar (dice "El sitio dice…", que en un ERP suena a error del navegador) y
 * bloquea el hilo, lo que congela cualquier guardado en curso.
 *
 * Cumple el patrón de diálogo modal del APG: foco atrapado, foco inicial en la
 * acción menos destructiva, Escape cierra y el foco vuelve a donde estaba.
 */

import { useEffect, useRef } from 'react';

import { Button } from '@/components/shell/primitives';

/** La salida que sí existe cuando la acción pedida no se puede hacer. */
export interface SalidaDelDialogo {
  label: string;
  /** Qué pasa si la aprieta. Va debajo del botón, no en un `title`. */
  hint?: string;
  onClick: () => void;
  loading?: boolean;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /**
   * Por qué falló el intento anterior, escrito por el servidor.
   *
   * Va DENTRO del diálogo y fijo. Antes vivía sólo en un aviso flotante que
   * aparecía detrás del modal, en la esquina, y que la persona no relacionaba
   * con el botón que acababa de apretar.
   */
  error?: string | null;
  /**
   * `false` cuando repetir el mismo gesto va a fallar igual (ver
   * `views/errores`). El botón de confirmar se retira: dejarlo armado al lado
   * del motivo es invitar a volver a intentarlo.
   */
  canRetry?: boolean;
  /** Lo que sí se puede hacer en lugar de lo que falló. */
  exit?: SalidaDelDialogo | null;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'primary',
  onConfirm,
  onCancel,
  loading = false,
  error = null,
  canRetry = true,
  exit = null,
}: ConfirmDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onCancel();
        return;
      }
      if (evento.key !== 'Tab') return;

      const foco = panel.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
      if (foco === undefined || foco.length === 0) return;
      const primero = foco[0];
      const ultimo = foco[foco.length - 1];
      if (primero === undefined || ultimo === undefined) return;

      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // El foco arranca en Cancelar: la tecla Enter reflejo no puede borrar datos.
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('#confirmar-cancelar')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [open, onCancel]);

  if (!open) return null;

  // Fallo definitivo: la acción pedida no se puede hacer, punto. Se retira el
  // botón que la ofrece —dejarlo armado al lado del motivo es invitar a
  // repetirla— y el diálogo pasa a explicar y a ofrecer la salida.
  const trabado = error !== null && !canRetry;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onCancel} aria-hidden="true" />
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmar-titulo"
        aria-describedby={error === null ? 'confirmar-texto' : 'confirmar-texto confirmar-error'}
        className="relative w-full max-w-md rounded-xl border border-line bg-surface p-5 shadow-lg animate-slide-up"
      >
        <h2 id="confirmar-titulo" className="text-lg font-semibold">
          {title}
        </h2>
        <p id="confirmar-texto" className="mt-2 text-sm text-fg-muted">
          {description}
        </p>

        {/* El motivo del servidor, fijo y dentro del diálogo. `role="alert"`
            para que un lector de pantalla lo anuncie apenas aparece: quien no ve
            la pantalla tenía todavía menos rastro que las demás personas. */}
        {error !== null && (
          <p
            id="confirmar-error"
            role="alert"
            className="mt-4 rounded-lg border border-danger bg-danger-soft px-3 py-2.5 text-sm text-fg"
          >
            <span className="font-medium">No se pudo. </span>
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel} id="confirmar-cancelar">
            {trabado ? 'Cerrar' : cancelLabel}
          </Button>
          {exit !== null && (
            <Button
              variant={trabado ? 'primary' : 'secondary'}
              onClick={exit.onClick}
              loading={exit.loading ?? false}
            >
              {exit.label}
            </Button>
          )}
          {!trabado && (
            <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
              {confirmLabel}
            </Button>
          )}
        </div>

        {/* Qué hace la salida. Debajo del botón y siempre visible: si hay que
            pasar el mouse por encima para enterarse, en un celular no existe. */}
        {exit !== null && exit.hint !== undefined && (
          <p className="mt-2 text-xs text-fg-faint sm:text-right">{exit.hint}</p>
        )}
      </div>
    </div>
  );
}
